import { useEffect, useState } from 'react';
import { getClients } from '../../services/clientService';
import { DuplicateWarning } from '../../components/forms/DuplicateWarning';
import type { Client } from '../../types/project';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { createClient } from '../../services/clientService';

interface NewClientDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const NewClientDialog = ({ open, onClose, onCreated }: NewClientDialogProps) => {
  const [clientName, setClientName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState<Client[]>([]);

  useEffect(() => {
    if (open) getClients().then(setExisting).catch(() => setExisting([]));
  }, [open]);

  const resetForm = () => {
    setClientName('');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setError('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleSubmit = async () => {
    if (!clientName.trim()) { setError('Client Name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await createClient({
        ClientName: clientName.trim(),
        ContactPerson: contactPerson.trim() || undefined,
        Email: email.trim() || undefined,
        Phone: phone.trim() || undefined,
      });
      resetForm();
      onCreated();
    } catch {
      setError('Failed to create client.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Client</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Client Name" required value={clientName}
          onChange={(e) => setClientName(e.target.value)} autoFocus />
        <DuplicateWarning
          value={clientName}
          candidates={existing}
          nameSelector={(c) => c.ClientName}
          label="Existing clients with similar names:"
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" label="Contact Person" value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)} />
          <TextField size="small" label="Phone" value={phone}
            onChange={(e) => setPhone(e.target.value)} />
        </Box>
        <TextField size="small" label="Email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} />
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Create Client
        </Button>
      </DialogActions>
    </Dialog>
  );
};
